import { Component, EventEmitter, Input, OnChanges, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { ColumnFilter, FilterOperator } from 'src/app/models/columnFilter.model';
import { GridSettingsService } from '../grid-settings.service';
import { OrganizationLevel } from 'src/app/models/organizationLevel.model';
import nameOf from 'src/app/shared/form/nameOf.helper';
import { GridView } from 'src/app/models/gridView.model';
import { UnitOption } from 'src/app/services/unit-filter.service';
import { trackById, trackByIndex } from 'src/app/shared/utils/track-by.util';

@Component({
  standalone: false,
  selector: 'app-filter-type',
  templateUrl: './filter-type.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./filter-type.component.scss']
})
export class FilterTypeComponent implements OnInit, OnChanges {
  @Input() filter!: ColumnFilter;
  @Input() separatorKeysCodes: number[] = [];
  @Input() getFilteredOptions!: (filter: ColumnFilter, isEnum?: boolean) => any[];
  @Input() organizationLevels: OrganizationLevel[] = [];
  @Output() filterChange = new EventEmitter<string>();
  @Output() addChip = new EventEmitter<any>();
  @Output() removeChip = new EventEmitter<any>();
  @Output() editChip = new EventEmitter<any>();

  startDate: Date | null = null;
  endDate: Date | null = null;

  min: number | null = null;
  max: number | null = null;

  availableUnits: UnitOption[] = [];
  unitAware = false;
  unitLabel = '';
  showUnitOptions = false;

  // Precomputed filter-type booleans
  isStringCache = false;
  isEnumCache = false;
  isObjectCache = false;
  isBooleanCache = false;
  isDateCache = false;
  isNumberCache = false;
  isOrgLevelCache = false;
  isRangeCache = false;
  isRelativeTodayCache = false;
  readonly today = new Date();
  filteredOrgLevels: OrganizationLevel[] = [];

  private lastSearchTerm: string = '';
  private cachedFilteredOrgLevels: OrganizationLevel[] = [];

  constructor(public gridSettingsService: GridSettingsService) { }

  ngOnInit() {
    this.initUnitCache();
    this.rebuildCache();
    if (this.isRangeCache && this.filter.valueArray?.length === 2) {
      if (this.isDateCache) {
        this.startDate = this.filter.valueArray[0] as Date;
        this.endDate = this.filter.valueArray[1] as Date;
      } else if (this.isNumberCache) {
        this.min = this.filter.valueArray[0] as number;
        this.max = this.filter.valueArray[1] as number;
      }
    }
  }

  private initUnitCache(): void {
    this.availableUnits = this.gridSettingsService.getAvailableUnits(this.filter);
    this.unitAware = this.gridSettingsService.isUnitAwareFilter(this.filter);
    this.unitLabel = this.gridSettingsService.getFilterUnitLabel(this.filter);
    this.showUnitOptions = this.gridSettingsService.hasUnitOptions(this.filter);
  }

  private rebuildCache(): void {
    this.isStringCache = this.gridSettingsService.isStringFilter(this.filter, true);
    this.isEnumCache = this.gridSettingsService.isEnumFilter(this.filter);
    this.isObjectCache = this.gridSettingsService.isObjectFilter(this.filter);
    this.isBooleanCache = this.gridSettingsService.isBooleanFilter(this.filter);
    this.isDateCache = this.gridSettingsService.isDateFilter(this.filter);
    this.isNumberCache = this.gridSettingsService.isNumberFilter(this.filter);
    this.isOrgLevelCache = this.filter.column.property === nameOf<GridView>(x => x.organizationLevelId) && this.isObjectCache;
    this.isRangeCache = this.filter.operator === FilterOperator.Between;
    this.isRelativeTodayCache = this.filter.operator === FilterOperator.RelativeToToday;
    this.rebuildFilteredOrgLevels();
  }

  private rebuildFilteredOrgLevels(): void {
    const search = this.filter.search?.toLowerCase() || '';
    this.lastSearchTerm = search;
    if (!search) {
      this.cachedFilteredOrgLevels = this.organizationLevels;
    } else {
      this.cachedFilteredOrgLevels = this.filterOrgLevelsRecursively(this.organizationLevels, search);
    }
    this.filteredOrgLevels = this.cachedFilteredOrgLevels;
  }

  ngOnChanges() {
    this.rebuildCache();
    if (this.isRangeCache && this.filter.valueArray?.length === 2) {
      this.startDate = this.filter.valueArray[0] as Date;
      this.endDate = this.filter.valueArray[1] as Date;
    }
  }

  onDateRangeChange() {
    if (this.startDate && this.endDate) {
      this.filter.valueArray = [this.startDate, this.endDate];
      this.filterChange.emit(this.filter.column.property);
    }
  }

  onNumberRangeChange() {
    if (this.min != null && this.max != null) {
      this.filter.valueArray = [this.min, this.max];
      this.filterChange.emit(this.filter.column.property);
    }
  }

  isStringFilter(options: boolean = false): boolean {
    return this.gridSettingsService.isStringFilter(this.filter, options);
  }

  isEnumFilter(): boolean {
    return this.gridSettingsService.isEnumFilter(this.filter);
  }

  isObjectFilter(): boolean {
    return this.gridSettingsService.isObjectFilter(this.filter);
  }

  isBooleanFilter(): boolean {
    return this.gridSettingsService.isBooleanFilter(this.filter);
  }

  isDateFilter(): boolean {
    return this.gridSettingsService.isDateFilter(this.filter);
  }

  isNumberFilter(): boolean {
    return this.gridSettingsService.isNumberFilter(this.filter);
  }

  isRangeMode(): boolean {
    return this.filter.operator === FilterOperator.Between;
  }

  isRelativeToToday(): boolean {
    return this.filter.operator === FilterOperator.RelativeToToday;
  }

  isOrganizationLevelFilter(): boolean {
    const propName = this.filter.column.property;
    return propName === nameOf<GridView>(x => x.organizationLevelId) && this.isObjectFilter();
  }


  onUnitChange(newUnit: number): void {
    this.filter.selectedUnit = newUnit;
    this.filter.value = undefined;
    this.filter.valueArray = undefined;
    this.min = null;
    this.max = null;
    this.unitLabel = this.gridSettingsService.getFilterUnitLabel(this.filter);
    this.isRangeCache = this.filter.operator === FilterOperator.Between;
    this.filterChange.emit(this.filter.column.property);
  }

  onOrgLevelSearchChange(): void {
    this.rebuildFilteredOrgLevels();
  }

  getFilteredOrganizationLevels(): OrganizationLevel[] {
    const search = this.filter.search?.toLowerCase() || '';

    if (search === this.lastSearchTerm && this.cachedFilteredOrgLevels.length > 0) {
      return this.cachedFilteredOrgLevels;
    }

    this.lastSearchTerm = search;

    if (!search) {
      this.cachedFilteredOrgLevels = this.organizationLevels;
      return this.organizationLevels;
    }

    this.cachedFilteredOrgLevels = this.filterOrgLevelsRecursively(this.organizationLevels, search);
    return this.cachedFilteredOrgLevels;
  }

  public trackByIndex = trackByIndex;
  public trackById = trackById;

  private filterOrgLevelsRecursively(nodes: OrganizationLevel[], search: string): OrganizationLevel[] {
    const filtered: OrganizationLevel[] = [];
    for (const node of nodes) {
      const matches = node.name?.toLowerCase().includes(search);
      const filteredChildren = node.children ? this.filterOrgLevelsRecursively(node.children, search) : [];

      if (matches || filteredChildren.length > 0) {
        filtered.push({
          ...node,
          children: filteredChildren.length > 0 ? filteredChildren : node.children
        });
      }
    }
    return filtered;
  }
}
